import {
  finishArtifactInputSchema,
  finishArtifactReceiptInputSchema,
  type FinishArtifactInput,
  type FinishArtifactReceiptInput,
  type FinishCandidateReference,
} from '../../../src/artifacts/authoring/finish-contract.mjs';
import type { ArtifactFinishService } from '../../../src/artifacts/authoring/finish.mjs';

export type ArtifactFinishTransport = {
  finish(input: FinishArtifactInput): ReturnType<ArtifactFinishService['finish']>;
  lookupFinishReceipt(
    input: FinishArtifactReceiptInput,
  ): ReturnType<ArtifactFinishService['lookupReceipt']>;
};

export type FinishOriginalScope = { identity: string; reference: FinishCandidateReference };
export type FinishOriginalRead =
  | { status: 'absent' }
  | { status: 'unreadable' }
  | { status: 'retained'; input: FinishArtifactReceiptInput };

export const finishOriginalKey = ({ identity, reference }: FinishOriginalScope) =>
  [
    'poneva.finish-original',
    identity,
    reference.artifactId,
    reference.candidateRef,
    reference.operationId,
  ].join('\0');

const stores = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** Only an actual missing key is absence. Read failure or invalid PRESENT hints cannot establish
 * that no Finish was sent; stay blocked until the same scoped hint can be recovered. */
export function loadFinishOriginal(scope: FinishOriginalScope): FinishOriginalRead {
  try {
    const storage = stores();
    if (!storage) return { status: 'unreadable' };
    const raw = storage.getItem(finishOriginalKey(scope));
    if (raw === null) return { status: 'absent' };
    const input = finishArtifactReceiptInputSchema.parse(JSON.parse(raw));
    return input.artifactId === scope.reference.artifactId &&
      input.operationId === scope.reference.operationId
      ? { status: 'retained', input }
      : { status: 'unreadable' };
  } catch {
    return { status: 'unreadable' };
  }
}

/** Retain BEFORE dispatch. If the tab cannot retain it, nothing is sent. Keep it even after a
 * response: a later mount still confirms this original through the current-authorized receipt. */
export function retainFinishOriginal(
  scope: FinishOriginalScope,
  raw: FinishArtifactInput,
): FinishArtifactInput {
  const input = finishArtifactInputSchema.parse(raw);
  const { reference } = scope;
  if (
    input.artifactId !== reference.artifactId ||
    input.candidateRef !== reference.candidateRef ||
    input.operationId !== reference.operationId
  )
    throw new Error('original does not match the candidate');
  const storage = stores();
  if (!storage) throw new Error('Finish original cannot be retained');
  const key = finishOriginalKey(scope);
  // Recheck before writing: another mount or a late successful read may reveal an original.
  // Never replace any existing hint, including invalid/unreadable hints.
  if (storage.getItem(key) !== null) throw new Error('Finish original already retained');
  const hint = JSON.stringify({
    artifactId: input.artifactId,
    operationId: input.operationId,
    inputDigest: input.inputDigest,
  });
  storage.setItem(key, hint);
  if (storage.getItem(key) !== hint) throw new Error('Finish original was not retained');
  return input;
}

/** The host retains prepareFinish's original tuple before dispatch. An HTTP
 * failure can follow a commit; recovery reads that receipt without Finish,
 * candidate reload, model invocation or a newly minted operation. */
export function createArtifactFinishActions(transport: ArtifactFinishTransport) {
  return Object.freeze({
    async finishOnce(raw: FinishArtifactInput): ReturnType<ArtifactFinishService['finish']> {
      const input = finishArtifactInputSchema.parse(raw);
      try {
        return await transport.finish(input);
      } catch {
        return {
          status: 'uncertain',
          code: 'receipt_reconciliation_required',
          artifactId: input.artifactId,
          operationId: input.operationId,
          inputDigest: input.inputDigest,
        };
      }
    },
    recoverOriginal(
      raw: FinishArtifactReceiptInput,
    ): ReturnType<ArtifactFinishService['lookupReceipt']> {
      return transport.lookupFinishReceipt(finishArtifactReceiptInputSchema.parse(raw));
    },
  });
}
